import React, { useState, useEffect } from 'react';
import './InstallPrompt.css';

function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  useEffect(() => {
    let interactionTimer;

    // Track user interaction to help trigger install prompt
    const trackInteraction = () => {
      if (!userInteracted) {
        setUserInteracted(true);
        console.log('User interaction detected - PWA install may become available');

        // Set a timer to check for install prompt after engagement
        interactionTimer = setTimeout(() => {
          console.log('Sufficient engagement time passed');
        }, 30000); // 30 seconds of engagement
      }
    };

    // Service Worker update detection
    const checkForUpdates = async (manual = false) => {
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (!registration) return;

          // Manually trigger update check if requested
          if (manual) {
            console.log('Manually checking for updates...');
            await registration.update();
          }

          // Listen for new service worker installation
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (!newWorker) return;

            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New update available
                console.log('🎉 App update available!');
                setUpdateAvailable(true);
                setWaitingWorker(newWorker);
                setUpdateError(null);
              }
            });
          });

          // Check if there's already a waiting worker
          if (registration.waiting && navigator.serviceWorker.controller) {
            console.log('🎉 App update available (waiting worker found)!');
            setUpdateAvailable(true);
            setWaitingWorker(registration.waiting);
            setUpdateError(null);
          }

          // Listen for messages from service worker
          navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data && event.data.type === 'SW_UPDATE_AVAILABLE') {
              console.log('🎉 Service worker notified of update availability!');
              checkForUpdates(true);
            }
          });
        } catch (error) {
          console.error('Error checking for updates:', error);
          setUpdateError('Failed to check for updates');
        }
      }
    };

    const handleBeforeInstallPrompt = (e) => {
      // Prevent the default mini-infobar on mobile
      e.preventDefault();
      console.log('🎉 beforeinstallprompt event fired - PWA is installable!');

      // Save the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);

      // Log comprehensive debug info
      console.log('PWA installation criteria verification:', {
        '✅ HTTPS': window.location.protocol === 'https:',
        '✅ Service Worker': 'serviceWorker' in navigator,
        '✅ Manifest': document.querySelector('link[rel="manifest"]') !== null,
        '✅ User Interaction': userInteracted,
        '✅ Not Standalone': !window.matchMedia('(display-mode: standalone)').matches,
        'User Agent': navigator.userAgent,
        'URL': window.location.href
      });
    };

    const handleAppInstalled = () => {
      console.log('appinstalled event fired');
      // Clear the deferredPrompt so the button is removed
      setDeferredPrompt(null);
      setIsInstallable(false);
    };

    // Add interaction listeners
    document.addEventListener('click', trackInteraction, { once: true });
    document.addEventListener('touchstart', trackInteraction, { once: true });
    document.addEventListener('keydown', trackInteraction, { once: true });

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check for updates on load
    checkForUpdates();

    // Set up periodic update checks (every 30 minutes)
    const updateInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        console.log('Periodic update check...');
        checkForUpdates(true);
      }
    }, 30 * 60 * 1000); // 30 minutes

    // Check for updates when page becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page visible - checking for updates...');
        checkForUpdates(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check if already in standalone mode
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('App is already installed (standalone mode)');
    }

    return () => {
      if (interactionTimer) {
        clearTimeout(interactionTimer);
      }
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      document.removeEventListener('click', trackInteraction);
      document.removeEventListener('touchstart', trackInteraction);
      document.removeEventListener('keydown', trackInteraction);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [userInteracted]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      console.log('No install prompt available');
      return;
    }

    console.log('Triggering install prompt');

    try {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      console.log('User choice:', outcome);

      // Clear the deferredPrompt regardless of outcome
      setDeferredPrompt(null);
      setIsInstallable(false);
    } catch (error) {
      console.error('Error showing install prompt:', error);
    }
  };

  const handleUpdateClick = async () => {
    if (!waitingWorker) {
      console.log('No waiting worker available');
      setUpdateError('No update available');
      return;
    }

    console.log('Triggering app update');
    setUpdateLoading(true);
    setUpdateError(null);

    try {
      // Set up controller change listener before triggering update
      const handleControllerChange = () => {
        console.log('New service worker activated, reloading...');
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
        window.location.reload();
      };

      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

      // Tell the waiting worker to skip waiting and become active
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });

      // Set a timeout in case the update doesn't complete
      setTimeout(() => {
        if (updateLoading) {
          setUpdateLoading(false);
          setUpdateError('Update is taking longer than expected. Please try refreshing the page.');
          navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
        }
      }, 10000); // 10 seconds timeout

    } catch (error) {
      console.error('Error triggering update:', error);
      setUpdateLoading(false);
      setUpdateError('Failed to update. Please try refreshing the page.');
    }
  };

  // Show update button if update is available, otherwise show install button
  if (updateAvailable) {
    return (
      <div className="update-container">
        <button
          className={`install-btn update-btn ${updateLoading ? 'updating' : ''}`}
          onClick={handleUpdateClick}
          disabled={updateLoading}
        >
          {updateLoading ? '⟳ Updating...' : 'Update Available'}
        </button>
        {updateError && (
          <div className="update-error">
            {updateError}
          </div>
        )}
      </div>
    );
  }

  // Only show the install button if we have a deferred prompt
  if (!isInstallable || !deferredPrompt) {
    return null;
  }

  return (
    <button className="install-btn" onClick={handleInstallClick}>
      Install
    </button>
  );
}

export default InstallPrompt;